/**
 * Wiederverwendbare Investor-Liste für /top-kandidaten UND /investoren.
 *
 * Render-Modi:
 *  - Desktop (md+): klassische 10-Spalten-Tabelle mit Inline-Edit
 *    (Welle/Einstufung/Betreuer). Touch-Targets auf min. 44px gehoben.
 *  - Mobile (<md):  Card-Liste, Tap auf Karte = Drawer öffnen.
 *                    Inline-Edits sind auf Mobile deaktiviert (zu klein für Touch);
 *                    Heinrichs nutzt für Mobile-Edits den Drawer.
 *
 * Defensive Optional-Chaining auf allen optionalen Investor-Feldern (Phase 5.1).
 */
import React, { useRef, useEffect, useState } from "react";
import { Lock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  PHASES,
  BETREUER,
  EINSTUFUNGEN,
  PhaseBadge,
  EinstufungBadge,
  HerkunftPill,
  BetreuerPill,
  TierBadge,
  AffinitaetDot,
} from "./Badges";
import { updateInvestor } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function InvestorTable({
  items,
  loading,
  emptyLabel = "Keine Investoren mit aktuellen Filtern.",
  showTierColumn = true,
  selectable = false,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleAll,
  onOpen,
  onSaved,
  testid = "investor-table",
}) {
  if (loading) {
    return <div className="pb-placeholder">Lade Investoren …</div>;
  }
  if (!items || items.length === 0) {
    return <div className="pb-placeholder">{emptyLabel}</div>;
  }

  const allChecked = selectable && items.length > 0 && items.every((x) => selectedIds.has(x.id));

  return (
    <>
      {/* Mobile: Card-Liste */}
      <div className="md:hidden flex flex-col divide-y divide-navy/10" data-testid={`${testid}-mobile`}>
        {items.map((inv) => (
          <InvestorCard
            key={inv.id}
            inv={inv}
            selectable={selectable}
            selected={selectedIds.has(inv.id)}
            onToggleSelect={onToggleSelect}
            onOpen={() => onOpen?.(inv.id)}
          />
        ))}
      </div>

      {/* Desktop: Tabelle */}
      <div className="hidden md:block overflow-x-auto">
        <table className="pb-table" data-testid={testid}>
          <thead>
            <tr>
              {selectable && (
                <th className="w-[40px]">
                  <input
                    type="checkbox"
                    data-testid="bulk-master-check"
                    checked={allChecked}
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    className="accent-[#C4922A] w-4 h-4"
                  />
                </th>
              )}
              {showTierColumn && <th className="w-[55px]">Tier</th>}
              <th className="w-[55px]">Welle</th>
              <th>Kandidat</th>
              <th>Investorentyp</th>
              <th>Einstufung</th>
              <th>Herkunft</th>
              <th>Phase</th>
              <th>Letzter Kontakt</th>
              <th>Zuständig</th>
              <th className="w-[80px]"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => (
              <InvestorRow
                key={inv.id}
                inv={inv}
                showTierColumn={showTierColumn}
                selectable={selectable}
                selected={selectedIds.has(inv.id)}
                onToggleSelect={onToggleSelect}
                onOpen={() => onOpen?.(inv.id)}
                onSaved={onSaved}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const InvestorRow = ({
  inv,
  showTierColumn,
  selectable,
  selected,
  onToggleSelect,
  onOpen,
  onSaved,
}) => {
  const [busyField, setBusyField] = useState(null);
  const debounceRef = useRef(null);
  const [welle, setWelle] = useState(inv?.welle ?? 1);
  useEffect(() => setWelle(inv?.welle ?? 1), [inv?.welle]);

  const persist = async (field, value, label) => {
    setBusyField(field);
    try {
      const updated = await updateInvestor(inv.id, { [field]: value });
      onSaved?.(updated);
      toast.success(`${label} gespeichert.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusyField(null);
    }
  };

  const onWelleChange = (e) => {
    const v = Math.max(1, Number(e.target.value || 1));
    setWelle(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (v !== inv.welle) persist("welle", v, "Welle");
    }, 500);
  };

  const stopRow = (e) => e.stopPropagation();
  const rowClick = (e) => {
    if (["INPUT", "SELECT", "BUTTON"].includes(e.target.tagName)) return;
    onOpen?.();
  };

  const isLocked = !!(inv?.gesperrt_fuer_bnp || inv?.gesperrt_fuer_uns);
  const lockTooltip = inv?.gesperrt_fuer_bnp && inv?.gesperrt_fuer_uns
    ? "Gesperrt für BNP & uns"
    : inv?.gesperrt_fuer_bnp ? "Gesperrt für BNP" : "Gesperrt für uns";

  return (
    <tr
      data-testid={`investor-row-${inv.id}`}
      className="cursor-pointer"
      onClick={rowClick}
    >
      {selectable && (
        <td onClick={stopRow}>
          <input
            type="checkbox"
            data-testid={`bulk-row-check-${inv.id}`}
            checked={selected}
            onChange={() => onToggleSelect?.(inv.id)}
            className="accent-[#022750] w-4 h-4"
          />
        </td>
      )}
      {showTierColumn && (
        <td><TierBadge tier={inv?.tier} testid={`tier-${inv.id}`} /></td>
      )}
      <td onClick={stopRow}>
        <input
          type="number"
          min="1"
          data-testid={`welle-${inv.id}`}
          value={welle}
          onChange={onWelleChange}
          disabled={busyField === "welle"}
          className="w-14 h-9 bg-transparent border-b border-navy/20 text-sm text-navy tabular-nums font-medium px-1 focus:outline-none focus:border-gold"
        />
      </td>
      <td>
        <div className="font-medium text-navy flex items-center gap-1.5">
          {isLocked && (
            <Lock size={11} className="text-tot flex-shrink-0" strokeWidth={2} title={lockTooltip} />
          )}
          <span className="break-words">{inv?.firma || "—"}</span>
        </div>
        {(inv?.region_kurz || inv?.subline) && (
          <div className="text-xs text-navy/55 mt-0.5">
            {inv?.subline && <span className="italic">{inv.subline}</span>}
            {inv?.subline && inv?.region_kurz && <span> · </span>}
            {inv?.region_kurz}
          </div>
        )}
        {(inv?.ebr_affinitaet || inv?.ls_affinitaet || inv?.de_affinitaet) && (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1" data-testid={`affinitaet-row-${inv.id}`}>
            <AffinitaetDot value={inv?.ebr_affinitaet} label="EBR" testid={`aff-ebr-${inv.id}`} />
            <AffinitaetDot value={inv?.ls_affinitaet}  label="LS"  testid={`aff-ls-${inv.id}`} />
            <AffinitaetDot value={inv?.de_affinitaet}  label="DE"  testid={`aff-de-${inv.id}`} />
          </div>
        )}
      </td>
      <td>
        <div className="text-navy/85">{inv?.investorentyp_kategorie || "—"}</div>
        {inv?.investorentyp_subtyp && (
          <div className="text-xs text-navy/55 mt-0.5">{inv.investorentyp_subtyp}</div>
        )}
      </td>
      <td onClick={stopRow}>
        <select
          data-testid={`einstufung-${inv.id}`}
          value={inv?.einstufung || "Nachrang"}
          onChange={(e) => persist("einstufung", e.target.value, "Einstufung")}
          disabled={busyField === "einstufung"}
          className="bg-transparent border-0 text-xs text-navy font-medium focus:outline-none cursor-pointer mb-1 h-7"
        >
          {EINSTUFUNGEN.map((e) => (
            <option key={e} value={e}>{e === "Heiss" ? "Heiß" : e}</option>
          ))}
        </select>
        <div><EinstufungBadge einstufung={inv?.einstufung} /></div>
      </td>
      <td>
        <div className="flex flex-wrap gap-1 max-w-[200px]">
          {(inv?.quellen || []).slice(0, 3).map((q) => (
            <HerkunftPill key={q} q={q} />
          ))}
        </div>
      </td>
      <td>
        <PhaseBadge phase={inv?.phase} />
      </td>
      <td>
        <div className="text-xs text-navy/75 tabular-nums">
          {fmtDate(inv?.letztes_aktivitaetsdatum) || (
            <span className="text-navy/40 italic">{inv?.letzter_kontakt_kontext || "noch kein Kontakt"}</span>
          )}
        </div>
        {fmtDate(inv?.letztes_aktivitaetsdatum) && inv?.letzter_kontakt_kontext && (
          <div className="text-[11px] text-navy/55 mt-0.5 max-w-[260px] line-clamp-2">{inv.letzter_kontakt_kontext}</div>
        )}
      </td>
      <td onClick={stopRow}>
        <select
          data-testid={`betreuer-${inv.id}`}
          value={inv?.betreuer_intern || ""}
          onChange={(e) => persist("betreuer_intern", e.target.value, "Betreuer")}
          disabled={busyField === "betreuer_intern"}
          className="bg-transparent border-0 text-xs text-navy/85 focus:outline-none cursor-pointer mb-1 h-7"
        >
          <option value="">— offen —</option>
          {BETREUER.map((b) => <option key={b}>{b}</option>)}
        </select>
        <div><BetreuerPill b={inv?.betreuer_intern} /></div>
      </td>
      <td onClick={stopRow}>
        <button
          type="button"
          data-testid={`details-${inv.id}`}
          onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
          className="text-xs uppercase tracking-widish text-navy hover:text-gold font-sans font-medium border border-navy/25 px-3 min-h-[36px] inline-flex items-center"
        >
          Details
        </button>
      </td>
    </tr>
  );
};

/**
 * Mobile Card-Variante: vollflächiger Tap-Bereich, alle wichtigen Infos.
 * Inline-Edits hier deaktiviert — Edits gehen über den Drawer (Touch-Friendly).
 */
const InvestorCard = ({ inv, selectable, selected, onToggleSelect, onOpen }) => {
  const isLocked = !!(inv?.gesperrt_fuer_bnp || inv?.gesperrt_fuer_uns);

  const handleCardClick = (e) => {
    // Wenn auf die Checkbox getappt wurde, nicht öffnen
    if (e.target.closest('[data-testid^="card-select-"]')) return;
    onOpen?.();
  };

  return (
    <div
      data-testid={`investor-card-${inv.id}`}
      onClick={handleCardClick}
      className="bg-white active:bg-ledger transition-colors cursor-pointer px-4 py-4 min-h-[44px]"
    >
      <div className="flex items-start gap-3">
        {selectable && (
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <label className="inline-flex items-center justify-center w-11 h-11 -m-2.5">
              <input
                type="checkbox"
                data-testid={`card-select-${inv.id}`}
                checked={selected}
                onChange={() => onToggleSelect?.(inv.id)}
                className="accent-[#022750] w-5 h-5"
              />
            </label>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Zeile 1: Tier-Badge + Firma + Lock */}
          <div className="flex items-start gap-2">
            <div className="shrink-0 mt-0.5">
              <TierBadge tier={inv?.tier} testid={`mtier-${inv.id}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {isLocked && <Lock size={12} className="text-tot shrink-0" strokeWidth={2} />}
                <span className="font-medium text-navy text-sm leading-snug break-words">
                  {inv?.firma || "—"}
                </span>
              </div>
              {(inv?.investorentyp_kategorie || inv?.region_kurz) && (
                <div className="text-xs text-navy/55 mt-0.5 break-words">
                  {inv?.investorentyp_kategorie}
                  {inv?.investorentyp_kategorie && inv?.region_kurz && " · "}
                  {inv?.region_kurz}
                </div>
              )}
              {(inv?.ebr_affinitaet || inv?.ls_affinitaet || inv?.de_affinitaet) && (
                <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
                  <AffinitaetDot value={inv?.ebr_affinitaet} label="EBR" />
                  <AffinitaetDot value={inv?.ls_affinitaet}  label="LS"  />
                  <AffinitaetDot value={inv?.de_affinitaet}  label="DE"  />
                </div>
              )}
            </div>
            <ChevronRight size={18} className="text-navy/30 shrink-0 mt-0.5" />
          </div>

          {/* Zeile 2: Badges */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <PhaseBadge phase={inv?.phase} />
            <EinstufungBadge einstufung={inv?.einstufung} />
            <BetreuerPill b={inv?.betreuer_intern} />
            {inv?.welle != null && (
              <span className="text-[10px] uppercase tracking-widish px-1.5 py-0.5 border border-navy/20 text-navy/70 font-sans tabular-nums">
                W{inv.welle}
              </span>
            )}
          </div>

          {/* Zeile 3: Letzter Kontakt */}
          <div className="text-[11px] text-navy/55 font-sans mt-2 tabular-nums">
            {fmtDate(inv?.letztes_aktivitaetsdatum) ? (
              <>
                <span className="text-navy/75">{fmtDate(inv.letztes_aktivitaetsdatum)}</span>
                {inv?.letzter_kontakt_kontext && (
                  <span className="text-navy/55"> · {inv.letzter_kontakt_kontext}</span>
                )}
              </>
            ) : (
              <span className="italic">{inv?.letzter_kontakt_kontext || "noch kein Kontakt"}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
